import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { RankBadge, RankProgress } from "@/lib/ranks";
import { PlatformIcons } from "@/lib/platforms";
import { useTwitchLive } from "@/hooks/useTwitchLive";

export const Logo = ({ className = "", size = "sm" }) => {
    const h = size === "lg" ? "h-24" : size === "md" ? "h-20" : "h-14";
    return (
        <Link to="/" data-testid="brand-logo" className={`inline-flex items-center group ${className}`}>
            <img src="/brand/logo.png" alt="Ranked Play Tournaments" className={`${h} w-auto object-contain drop-shadow-[0_0_22px_rgba(168,85,247,0.45)] transition-transform duration-500 group-hover:scale-[1.04]`} />
        </Link>
    );
};

const BASE_LINKS = [
    { to: "/tournaments", label: "Tournaments" },
    { to: "/live", label: "Live" },
    { to: "/rules", label: "Rules" },
    { to: "/roadmap", label: "Roadmap" },
    { to: "/champions", label: "Champions" },
    { to: "/leaderboard", label: "Rankings" },
    { to: "/teams", label: "Teams" },
];

export default function Header() {
    const { user, logout, invitesCount } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const twitch = useTwitchLive("rankedplaytournaments");
    const links = user?.role === "admin" ? [...BASE_LINKS, { to: "/admin", label: "Admin" }] : BASE_LINKS;

    // Auto-close the mobile drawer on route change
    useEffect(() => { setMobileOpen(false); }, [location.pathname]);
    // Prevent body scroll while drawer open
    useEffect(() => {
        document.body.style.overflow = mobileOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [mobileOpen]);

    const doLogout = async () => { await logout(); nav("/"); };

    return (
        <motion.header
            initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 backdrop-blur-xl bg-[#0a0a0a]/60"
            data-testid="site-header"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-[68px] md:h-[84px] flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.to.replace('/','')}`}
                            title={l.to === "/live" && twitch.live ? `On air · ${twitch.uptime || "live"}` : undefined}
                            className={({ isActive }) =>
                                `relative font-mono uppercase text-[11px] tracking-[0.24em] px-4 py-2 transition-colors ${isActive ? "text-volt" : "text-white/70 hover:text-white"}`
                            }
                        >
                            <span className="inline-flex items-center gap-2">
                                {l.label}
                                {l.to === "/live" && twitch.live && (
                                    <span
                                        data-testid="nav-live-badge"
                                        className="inline-flex items-center gap-1 border border-signal/70 text-signal px-1.5 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase leading-none"
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />
                                        Live
                                    </span>
                                )}
                            </span>
                            {l.to === "/teams" && user && invitesCount > 0 && (
                                <span
                                    className="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] rounded-full bg-signal text-white font-mono text-[10px] font-bold flex items-center justify-center px-1"
                                    data-testid="invites-badge"
                                >
                                    {invitesCount > 9 ? "9+" : invitesCount}
                                </span>
                            )}
                        </NavLink>
                    ))}
                    {user && (
                        <NavLink
                            to={`/player/${encodeURIComponent(user.gamer_tag)}`}
                            data-testid="nav-profile"
                            className={({ isActive }) =>
                                `relative font-mono uppercase text-[11px] tracking-[0.24em] px-4 py-2 transition-colors ${isActive ? "text-volt" : "text-white/70 hover:text-white"}`
                            }
                        >
                            Profile
                        </NavLink>
                    )}
                </nav>
                <div className="flex items-center gap-3">
                    {user ? (
                        <>
                            <Link
                                to={`/player/${encodeURIComponent(user.gamer_tag)}`}
                                data-testid="header-profile-link"
                                className="hidden md:flex flex-col items-end gap-1 group"
                                title="View your profile"
                            >
                                <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-white/60 group-hover:text-white transition-colors">
                                    <span className="text-volt">/</span>{user.gamer_tag}
                                    <PlatformIcons user={user} size="xs" testId="header-platforms" />
                                    <RankBadge elo={user.elo} size="xs" testId="header-rank-badge" />
                                    {user.id_verification_status === "verified" && (
                                        <span className="text-volt" title="ID verified" data-testid="verify-badge-verified">◆</span>
                                    )}
                                    {user.id_verification_status === "pending" && (
                                        <span className="text-white/50" title="ID under review" data-testid="verify-badge-pending">◇</span>
                                    )}
                                </span>
                                <RankProgress elo={user.elo} className="w-[180px]" testId="header-rank-progress" />
                            </Link>
                            <button data-testid="logout-btn" onClick={doLogout} className="hidden md:inline-flex btn-ghost !py-2.5 !px-4 !text-[10px]">Sign out</button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" data-testid="header-login-link" className="hidden md:inline font-mono text-[11px] tracking-[0.24em] text-white/70 hover:text-white px-3">Sign in</Link>
                            <Link to="/register" data-testid="header-register-btn" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 !text-[10px]">Enter Arena</Link>
                        </>
                    )}

                    {/* Mobile hamburger */}
                    <button
                        aria-label="Open menu"
                        aria-expanded={mobileOpen}
                        onClick={() => setMobileOpen(v => !v)}
                        data-testid="mobile-menu-toggle"
                        className="md:hidden relative w-11 h-11 flex items-center justify-center border border-white/15 bg-[#0a0a0a] active:bg-white/5"
                    >
                        <span className="sr-only">Menu</span>
                        <span aria-hidden className={`absolute w-5 h-[2px] bg-white transition-all ${mobileOpen ? "rotate-45" : "-translate-y-1.5"}`} />
                        <span aria-hidden className={`absolute w-5 h-[2px] bg-white transition-all ${mobileOpen ? "opacity-0" : ""}`} />
                        <span aria-hidden className={`absolute w-5 h-[2px] bg-white transition-all ${mobileOpen ? "-rotate-45" : "translate-y-1.5"}`} />
                        {user && invitesCount > 0 && !mobileOpen && (
                            <span
                                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-signal text-white font-mono text-[9px] font-bold flex items-center justify-center px-1"
                                data-testid="mobile-invites-badge"
                            >
                                {invitesCount > 9 ? "9+" : invitesCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* MOBILE DROP-DOWN DRAWER */}
            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        key="mobile-drawer"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl max-h-[calc(100vh-68px)] overflow-y-auto"
                        data-testid="mobile-menu"
                    >
                        {user && (
                            <div className="px-6 py-5 border-b border-white/10" data-testid="mobile-user-chip">
                                <Link to={`/player/${encodeURIComponent(user.gamer_tag)}`} className="flex items-center gap-3 flex-wrap">
                                    <span className="font-display font-black text-xl leading-none"><span className="text-volt">/</span>{user.gamer_tag}</span>
                                    <RankBadge elo={user.elo} size="xs" />
                                    <PlatformIcons user={user} size="xs" />
                                </Link>
                                <div className="mt-3 max-w-[220px]"><RankProgress elo={user.elo} /></div>
                            </div>
                        )}
                        <nav className="flex flex-col py-2" data-testid="mobile-nav-links">
                            {links.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.to.replace('/','')}`}
                                    className={({ isActive }) =>
                                        `relative flex items-center justify-between font-mono uppercase text-[13px] tracking-[0.22em] px-6 py-4 border-b border-white/5 transition-colors ${isActive ? "text-volt bg-volt/[0.06]" : "text-white/80 hover:text-white active:bg-white/5"}`
                                    }
                                >
                                    <span className="inline-flex items-center gap-2">
                                        {l.label}
                                        {l.to === "/live" && twitch.live && (
                                            <span
                                                data-testid="mobile-nav-live-badge"
                                                className="inline-flex items-center gap-1 border border-signal/70 text-signal px-1.5 py-0.5 font-mono text-[9px] tracking-[0.28em] uppercase leading-none"
                                            >
                                                <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />
                                                Live
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-white/30 text-xl">→</span>
                                    {l.to === "/teams" && user && invitesCount > 0 && (
                                        <span className="absolute right-14 top-1/2 -translate-y-1/2 min-w-[20px] h-5 rounded-full bg-signal text-white font-mono text-[10px] font-bold flex items-center justify-center px-1.5">
                                            {invitesCount > 9 ? "9+" : invitesCount}
                                        </span>
                                    )}
                                </NavLink>
                            ))}
                            {user && (
                                <NavLink
                                    to={`/player/${encodeURIComponent(user.gamer_tag)}`}
                                    data-testid="mobile-nav-profile"
                                    className={({ isActive }) =>
                                        `flex items-center justify-between font-mono uppercase text-[13px] tracking-[0.22em] px-6 py-4 border-b border-white/5 transition-colors ${isActive ? "text-volt bg-volt/[0.06]" : "text-white/80 hover:text-white active:bg-white/5"}`
                                    }
                                >
                                    <span>Profile</span>
                                    <span className="text-white/30 text-xl">→</span>
                                </NavLink>
                            )}
                        </nav>
                        <div className="p-6 flex flex-col gap-3">
                            {user ? (
                                <button onClick={doLogout} className="btn-ghost w-full text-center" data-testid="mobile-logout-btn">Sign out</button>
                            ) : (
                                <>
                                    <Link to="/login" className="btn-ghost w-full text-center" data-testid="mobile-login-link">Sign in</Link>
                                    <Link to="/register" className="btn-primary w-full text-center" data-testid="mobile-register-btn">Enter Arena</Link>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
